import { ReadyToRoll, SubmissionsByDate } from '@/src/types/apps/resto365ReadyToRoll'
import {
  RESTO365_AYRTR,
  RESTO365_AYRTR_ALL_FORMS,
  RESTO365_AYRTR_ALL_FORMS_GROUPED,
  RESTO365_AYRTR_ALL_FORMS_GROUPED_DAYPART_STATS,
  RESTO365_AYRTR_ALL_FORMS_SUBMISSION_BY_ID_DATE,
  RESTO365_AYRTR_FORMS,
  RESTO365_AYRTR_FORMS_CREATE,
  RESTO365_AYRTR_FORMS_INITIAL_STATE,
  RESTO365_AYRTR_GET_FORM_BY_ID,
  RESTO365_AYRTR_GET_FORM_UPLOADS_BY_ID,
  RESTO365_AYRTR_MEDIA_UPLOADS
} from './action'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import { buildFormTemplateUrl } from '@/src/utils/formTemplateEndpoints'
import { logErrorToRum } from '@/src/observability/datadog'
import { isUnauthorizedError } from '@/src/utils/errorUtils'

interface mediaUploads {
  section: string
  field: string
  urls: string[]
}

interface AYRTRInitialState {
  readyToRoll: ReadyToRoll
  readyToRollSuccess: any
  allRTRData: any[]
  allGroupedRTRData: SubmissionsByDate
  loading: boolean
  success: string | null
  errors: string | object | null // Allow objects for 403/401 errors so components can detect them
  readyToRollById: any
  reportingError: string | object | null // Allow objects for 403/401 errors so components can detect them
  reportingSuccess: string | null
  imageUpload: any
  imageUploadSuccess: string | null
  imageUploadError: string | null
  allMediaUploads: mediaUploads[]
  formSuccess: string | null
  formError: string | null
  dayPartSubmission: {
    counts: {
      [date: string]: {
        breakfast: number
        lunch: number
        dinner: number
        latenight: number
      }
    }
    percentages: {
      [date: string]: {
        breakfast: number | null
        lunch: number | null
        dinner: number | null
        latenight: number | null
      }
    }
  }
}

export const initialState: AYRTRInitialState = {
  readyToRoll: {
    name: '',
    description: '',
    sections: [],
    dayPart: [],
    id: '',
    version: ''
  },
  readyToRollSuccess: {
    checklist: { name: '', description: '', sections: [], dayPart: '', id: '', version: '' },
    meta: {
      restaurantId: '',
      submittedBy: '',
      startTime: ''
    },
    submissionId: ''
  },
  allRTRData: [],
  allGroupedRTRData: {},
  loading: false,
  errors: null,
  success: null,
  readyToRollById: {},
  reportingError: null,
  reportingSuccess: null,
  imageUpload: null,
  imageUploadError: null,
  imageUploadSuccess: null,
  allMediaUploads: [],
  formSuccess: null,
  formError: null,
  dayPartSubmission: {
    counts: {},
    percentages: {}
  }
}

interface GetAYRTRFORMS {
  data: any
}

interface GetAYRTRListResponse {
  data: {
    items: ReadyToRoll[]
  }
}

interface DayPartData {
  counts: {
    [date: string]: {
      breakfast: number
      lunch: number
      dinner: number
      latenight: number
    }
  }
  percentages: {
    [date: string]: {
      breakfast: number | null
      lunch: number | null
      dinner: number | null
      latenight: number | null
    }
  }
}

interface GetAYRTRDayPartResponse {
  data: DayPartData
}

interface DayPartSubmission {
  id: string
  date: string
}

export const gygAYRTRAllForms = createAsyncThunk<GetAYRTRListResponse, string, Redux>(
  RESTO365_AYRTR_ALL_FORMS,
  async (id, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient(`form-submissions/restaurant/${id}`)

      return responseData.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const gygAYRTRFormSubmissionbyIdAndDate = createAsyncThunk<GetAYRTRDayPartResponse, DayPartSubmission, Redux>(
  RESTO365_AYRTR_ALL_FORMS_SUBMISSION_BY_ID_DATE,
  async ({ id, date }, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient(`form-submissions/restaurant/${id}/date/${date}`)

      return responseData.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const gygAYRTRAllFormsGrouped = createAsyncThunk<
  { data: { data: { data: { items: { submissionsByDate: SubmissionsByDate } } } } },
  string,
  Redux
>(RESTO365_AYRTR_ALL_FORMS_GROUPED, async (date, { extra, rejectWithValue }) => {
  try {
    const responseData = await extra.httpClient(`form-submissions/restaurant/grouped/all?date=${date}`)

    return responseData
  } catch (error: any) {
    if (error.response) {
      return rejectWithValue(error.response.data)
    }

    return rejectWithValue('An error occurred')
  }
})

export const gygAYRTRAllFormsGroupedDayPartStats = createAsyncThunk<GetAYRTRDayPartResponse, string, Redux>(
  RESTO365_AYRTR_ALL_FORMS_GROUPED_DAYPART_STATS,
  async (date, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient(`form-submissions/restaurant/grouped/daypart-stats?date=${date}`)

      return responseData.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const getAyrtrSectionForms = createAsyncThunk<GetAYRTRFORMS, string, Redux>(
  RESTO365_AYRTR_FORMS,
  async (restoId, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(
        buildFormTemplateUrl('a59f1b43-186b-49e6-8a3e-7acd1bd84232', restoId, 'RWRTR')
      )

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const gygFormBySubmissionId = createAsyncThunk<GetAYRTRFORMS, string, Redux>(
  RESTO365_AYRTR_GET_FORM_BY_ID,
  async (formId, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient(`form-submissions/${formId}`)

      return responseData.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const gygFormByUploadsSubmissionId = createAsyncThunk<GetAYRTRFORMS, string, Redux>(
  RESTO365_AYRTR_GET_FORM_UPLOADS_BY_ID,
  async (formId, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient(`form-submissions/extract-media-urls/${formId}`)

      return responseData.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const AYRTRMediaUploads = createAsyncThunk<GetAYRTRFORMS, any, Redux>(
  RESTO365_AYRTR_MEDIA_UPLOADS,
  async (formData, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.post('form-submissions/upload-image', formData)

      return responseData.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const AYRTRformSubmit = createAsyncThunk<GetAYRTRFORMS, any, Redux>(
  RESTO365_AYRTR_FORMS_CREATE,
  async (formData, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.post('form-submissions', formData)

      return responseData.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      // Handle network errors, timeouts, and other non-response errors
      return rejectWithValue({
        message: error.message || 'Network error occurred',
        code: error.code || 'NETWORK_ERROR',
        details: 'Please check your internet connection and try again.'
      })
    }
  }
)

export const clearErrorDataAYRTR = createAsyncThunk<AYRTRInitialState, void, Redux>(
  RESTO365_AYRTR_FORMS_INITIAL_STATE,
  async () => {
    return initialState
  }
)

export const resto365rtrForms = createSlice({
  name: RESTO365_AYRTR,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      .addCase(gygAYRTRAllForms.fulfilled, (state, action) => {
        state.allRTRData = action.payload.data.items
        state.reportingError =
          Object.keys(action.payload).length === 0
            ? 'Please contact administrator if you are not able to view the form.'
            : null
        state.loading = false
        state.reportingSuccess = Object.keys(action.payload).length === 0 ? null : 'Data fetched successfully'
      })
      .addCase(gygAYRTRAllForms.pending, state => {
        state.reportingError = null
        state.loading = true
        state.reportingSuccess = ''
      })
      .addCase(gygAYRTRAllForms.rejected, (state, action) => {
        const errorPayload = action.payload as any

        if (!isUnauthorizedError(errorPayload)) {
          const errorMessage = 'Please contact administrator if you are not able to view the form.'
          state.reportingError = errorMessage
        } else {
          // For 403/401 errors, store the error object so component can detect and redirect
          // Component checks errors && isUnauthorizedError(errors) to trigger redirect
          state.reportingError = errorPayload || null
        }
        state.loading = false

        // Log error to RUM for observability
        logErrorToRum(
          action.error || new Error('Failed to fetch all forms'),
          {
            action: RESTO365_AYRTR_ALL_FORMS,
            errorPayload: action.payload,
            errorType: 'AWRTR_FETCH_FORMS_ERROR'
          },
          'redux'
        )
      })

      .addCase(gygAYRTRFormSubmissionbyIdAndDate.fulfilled, (state, action) => {
        if ((action.payload?.data as any)?.dayPartSubmission) {
          state.dayPartSubmission = (action.payload.data as any).dayPartSubmission
        } else if (action.payload?.data) {
          state.dayPartSubmission = {
            counts: action.payload.data.counts || {},
            percentages: action.payload.data.percentages || {}
          }
        } else {
          state.dayPartSubmission = {
            counts: {},
            percentages: {}
          }
        }
        state.loading = false
        state.formSuccess = null
        state.formError = null
      })
      .addCase(gygAYRTRFormSubmissionbyIdAndDate.pending, state => {
        state.reportingError = null
        state.loading = true
      })
      .addCase(gygAYRTRFormSubmissionbyIdAndDate.rejected, (state, action) => {
        const errorPayload = action.payload as any

        if (!isUnauthorizedError(errorPayload)) {
          // Only set error for non-403/401 errors
          state.errors = errorPayload?.message || 'Failed to fetch form submission by date'
        } else {
          // For 403/401 errors, store the error object so component can detect and redirect
          // Component checks errors && isUnauthorizedError(errors) to trigger redirect
          state.errors = errorPayload || null
        }
        state.loading = false

        // Log error to RUM for observability
        logErrorToRum(
          action.error || new Error('Failed to fetch form submission by date'),
          {
            action: RESTO365_AYRTR_ALL_FORMS_SUBMISSION_BY_ID_DATE,
            errorPayload: action.payload,
            errorType: 'AWRTR_FETCH_SUBMISSION_BY_DATE_ERROR'
          },
          'redux'
        )
      })

      .addCase(gygAYRTRAllFormsGrouped.fulfilled, (state, action) => {
        const submissionsByDate = action.payload?.data?.data?.data?.items?.submissionsByDate || {}
        state.allGroupedRTRData = submissionsByDate
        state.reportingError =
          Object.keys(submissionsByDate).length === 0
            ? 'Please contact administrator if you are not able to view the form.'
            : null
        state.loading = false
        state.reportingSuccess = Object.keys(submissionsByDate).length === 0 ? null : 'Data fetched successfully'
        state.formSuccess = null
        state.formError = null
      })
      .addCase(gygAYRTRAllFormsGrouped.pending, state => {
        state.reportingError = null
        state.loading = true
        state.reportingSuccess = ''
      })
      .addCase(gygAYRTRAllFormsGrouped.rejected, (state, action) => {
        const errorPayload = action.payload as any

        if (!isUnauthorizedError(errorPayload)) {
          const errorMessage = 'Please contact administrator if you are not able to view the form.'
          state.reportingError = errorMessage
        } else {
          // For 403/401 errors, store the error object so component can detect and redirect
          // Component checks errors && isUnauthorizedError(errors) to trigger redirect
          state.reportingError = errorPayload || null
        }
        state.loading = false

        // Log error to RUM for observability
        logErrorToRum(
          action.error || new Error('Failed to fetch grouped forms'),
          {
            action: RESTO365_AYRTR_ALL_FORMS_GROUPED,
            errorPayload: action.payload,
            errorType: 'AWRTR_FETCH_GROUPED_FORMS_ERROR'
          },
          'redux'
        )
      })

      .addCase(gygAYRTRAllFormsGroupedDayPartStats.fulfilled, (state, action) => {
        try {
          const response = action.payload

          // The response has counts and percentages directly in the data object
          if (response?.data) {
            state.dayPartSubmission = {
              counts: response.data.counts || {},
              percentages: response.data.percentages || {}
            }
          } else {
            state.dayPartSubmission = {
              counts: {},
              percentages: {}
            }
          }
        } catch (error) {
          console.error('Error processing day part stats:', error)
          state.dayPartSubmission = {
            counts: {},
            percentages: {}
          }
        }

        state.loading = false
        state.formSuccess = null
        state.formError = null
      })
      .addCase(gygAYRTRAllFormsGroupedDayPartStats.pending, state => {
        state.reportingError = null
        state.loading = true
        state.reportingSuccess = ''
      })
      .addCase(gygAYRTRAllFormsGroupedDayPartStats.rejected, (state, action) => {
        const errorPayload = action.payload as any

        if (!isUnauthorizedError(errorPayload)) {
          const errorMessage = 'Please contact administrator if you are not able to view the form.'
          state.reportingError = errorMessage
        } else {
          // For 403/401 errors, store the error object so component can detect and redirect
          // Component checks errors && isUnauthorizedError(errors) to trigger redirect
          state.reportingError = errorPayload || null
        }
        state.loading = false

        // Log error to RUM for observability
        logErrorToRum(
          action.error || new Error('Failed to fetch day part stats'),
          {
            action: RESTO365_AYRTR_ALL_FORMS_GROUPED_DAYPART_STATS,
            errorPayload: action.payload,
            errorType: 'AWRTR_FETCH_DAYPART_STATS_ERROR'
          },
          'redux'
        )
      })

      .addCase(gygFormBySubmissionId.fulfilled, (state, action) => {
        state.readyToRollById = action.payload.data
        state.errors =
          Object.keys(action.payload).length === 0
            ? 'Please contact administrator if you are not able to view the form.'
            : null
        state.loading = false
        state.success = Object.keys(action.payload).length === 0 ? null : 'Data fetched successfully'
        state.formSuccess = null
        state.formError = null
      })
      .addCase(gygFormBySubmissionId.pending, state => {
        state.errors = null
        state.loading = true
        state.success = null
      })
      .addCase(gygFormBySubmissionId.rejected, (state, action) => {
        const errorMessage = 'Please contact administrator if you are not able to view the form.'
        state.errors = errorMessage
        state.loading = false

        // Log error to RUM for observability
        logErrorToRum(
          action.error || new Error(errorMessage),
          {
            action: RESTO365_AYRTR_GET_FORM_BY_ID,
            errorPayload: action.payload,
            errorType: 'AWRTR_FETCH_FORM_BY_ID_ERROR'
          },
          'redux'
        )
      })

      .addCase(gygFormByUploadsSubmissionId.fulfilled, (state, action) => {
        state.allMediaUploads = action.payload.data
        state.errors =
          Object.keys(action.payload).length === 0
            ? 'Please contact administrator if you are not able to view the form.'
            : null
        state.loading = false
        state.success = Object.keys(action.payload).length === 0 ? null : 'Data fetched successfully'
        state.formSuccess = null
        state.formError = null
      })
      .addCase(gygFormByUploadsSubmissionId.pending, state => {
        state.errors = null
        state.loading = true
        state.success = null
        state.formSuccess = null
        state.formError = null
      })
      .addCase(gygFormByUploadsSubmissionId.rejected, (state, action) => {
        const errorMessage = 'Please contact administrator if you are not able to view the form.'
        state.errors = errorMessage
        state.loading = false

        // Log error to RUM for observability
        logErrorToRum(
          action.error || new Error(errorMessage),
          {
            action: RESTO365_AYRTR_GET_FORM_UPLOADS_BY_ID,
            errorPayload: action.payload,
            errorType: 'AWRTR_FETCH_FORM_UPLOADS_ERROR'
          },
          'redux'
        )
      })

      .addCase(getAyrtrSectionForms.fulfilled, (state, action) => {
        state.readyToRoll = action.payload.data
        state.errors =
          Object.keys(action.payload).length === 0
            ? 'Please contact administrator if you are not able to view the form.'
            : null
        state.loading = false
        state.success = Object.keys(action.payload).length === 0 ? null : 'Data fetched successfully'
        state.formSuccess = null
        state.formError = null
      })
      .addCase(getAyrtrSectionForms.pending, state => {
        state.errors = null
        state.loading = true
        state.success = null
      })
      .addCase(getAyrtrSectionForms.rejected, (state, action) => {
        const errorPayload = action.payload as any

        if (!isUnauthorizedError(errorPayload)) {
          const errorMessage = 'Please contact administrator if you are not able to view the form.'
          state.errors = errorMessage
        } else {
          // For 403/401 errors, store the error object so component can detect and redirect
          // Component checks errors && isUnauthorizedError(errors) to trigger redirect
          state.errors = errorPayload || null
        }
        state.loading = false

        // Log error to RUM for observability
        logErrorToRum(
          action.error || new Error('Failed to fetch section forms'),
          {
            action: RESTO365_AYRTR_FORMS,
            errorPayload: action.payload,
            errorType: 'AWRTR_FETCH_SECTION_FORMS_ERROR'
          },
          'redux'
        )
      })

      .addCase(AYRTRMediaUploads.fulfilled, (state, action) => {
        state.imageUpload = action.payload
        state.imageUploadError = null
        state.loading = false
        state.imageUploadSuccess = 'Image uploaded successfully'
      })
      .addCase(AYRTRMediaUploads.pending, state => {
        state.errors = null
        state.loading = true
        state.success = null
      })
      .addCase(AYRTRMediaUploads.rejected, (state, action) => {
        const errorMessage = 'Please contact admin if you are not able to upload the image.'
        state.imageUploadError = errorMessage
        state.loading = false

        // Log error to RUM for observability
        logErrorToRum(
          action.error || new Error(errorMessage),
          {
            action: RESTO365_AYRTR_MEDIA_UPLOADS,
            errorPayload: action.payload,
            errorType: 'AWRTR_MEDIA_UPLOAD_ERROR'
          },
          'redux'
        )
      })

      .addCase(AYRTRformSubmit.fulfilled, (state, action) => {
        state.readyToRollSuccess = action.payload.data
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data submitted successfully'
      })
      .addCase(AYRTRformSubmit.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(AYRTRformSubmit.rejected, (state, action) => {
        // Extract meaningful error message from the rejected value
        const errorPayload = action.payload as any
        let errorMessage = 'Please contact administrator if you are not able to submit form.'

        if (errorPayload?.message) {
          errorMessage = errorPayload.message
          state.formError = errorPayload.message
        } else if (errorPayload?.details) {
          errorMessage = errorPayload.details
          state.formError = errorPayload.details
        } else {
          state.formError = errorMessage
        }
        state.loading = false

        // Log error to RUM for observability
        logErrorToRum(
          action.error || new Error(errorMessage),
          {
            action: RESTO365_AYRTR_FORMS_CREATE,
            errorPayload: action.payload,
            errorType: 'AWRTR_FORM_SUBMIT_ERROR',
            errorCode: errorPayload?.code,
            errorDetails: errorPayload?.details
          },
          'redux'
        )
      })

      .addCase(clearErrorDataAYRTR.fulfilled, state => {
        state.success = null
        state.errors = null
        state.formError = null
        state.formSuccess = null
        state.reportingError = null
        state.reportingSuccess = null
        state.loading = false
      })
  }
})

export default resto365rtrForms.reducer
